/** The reusable rules section — mounted by BOTH the notification editor and
 *  the ruleset editor.
 *
 * Fully controlled: `value` is the complete ordered rule list, `onChange`
 * replaces it (owners mark their draft dirty and send the COMPLETE list on
 * save — the backend reconciles by id and deletes omitted rows). Order is
 * cosmetic: rules have no persisted sequence and `matchType` makes it
 * semantically irrelevant, so there is no reordering UI (dashboard parity).
 *
 * Rows collapse to one line each (NOT chip · condition · summary); existing
 * rows start collapsed, newly added ones expanded. "Include ruleset" gets a
 * dedicated button — it is the composition primitive and shouldn't be buried
 * among 45 templates.
 */
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DataObjectIcon from '@mui/icons-material/DataObject';
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { MATCH_ALL, MATCH_ANY } from '@/api/notificationRulesets';
import { FieldHelp } from '@/components/FieldHelp';
import type { ContextSchemaSource } from '@/components/TemplateCodeEditor/types';

import { RULES_HELP } from '../fieldHelp';
import { AddRuleMenu } from './AddRuleMenu';
import { TPL } from './ruleCatalog';
import { newExprRule, newRule } from './ruleSerialize';
import { RuleRow } from './RuleRow';
import type { RuleDraft } from './ruleTypes';

export interface RulesEditorProps {
  value: RuleDraft[];
  onChange: (rules: RuleDraft[]) => void;
  matchType: number;
  onMatchTypeChange: (matchType: number) => void;
  /** Scope for tenant-bound pickers (tags, tours, rulesets). */
  tenantId: number | 'system';
  /** Ruleset being edited — excluded from Include-ruleset pickers. */
  excludeRulesetId?: number;
  /** Per-row errors keyed by row index (server 400s and blocking client
   *  validation, see useRuleIssues); '*' = general. */
  errors?: Record<string, string>;
  /** Per-row non-blocking warnings keyed by row index (legacy rules that break
   *  the rule-args schema but are saved unchanged). */
  warnings?: Record<string, string>;
  disabled?: boolean;
  /** What an empty rule list matches, e.g. "every event". */
  emptyMatchesLabel?: string;
  /** Event context schemas for expression-rule completions. */
  contextSchemas?: ContextSchemaSource[];
}

export function RulesEditor({
  value,
  onChange,
  matchType,
  onMatchTypeChange,
  tenantId,
  excludeRulesetId,
  errors,
  warnings,
  disabled,
  emptyMatchesLabel = 'everything',
  contextSchemas,
}: RulesEditorProps) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const setExpanded = (id: number, expanded: boolean) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (expanded) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const append = (rule: RuleDraft) => {
    onChange([...value, rule]);
    setExpanded(rule.id, true);
  };

  const replaceAt = (index: number, rule: RuleDraft) =>
    onChange(value.map((r, i) => (i === index ? rule : r)));

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Rules
        </Typography>
        {value.length > 1 && (
          <>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={matchType === MATCH_ANY ? 'any' : 'all'}
              disabled={disabled}
              onChange={(_, v) => {
                if (v !== null) onMatchTypeChange(v === 'any' ? MATCH_ANY : MATCH_ALL);
              }}
              aria-label="Rule match mode"
            >
              <ToggleButton value="all" sx={{ px: 1.5, py: 0.25 }}>
                Match ALL
              </ToggleButton>
              <ToggleButton value="any" sx={{ px: 1.5, py: 0.25 }}>
                Match ANY
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary">
              of {value.length} rules
            </Typography>
          </>
        )}
        <FieldHelp help={RULES_HELP.matchType} />
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<AccountTreeIcon />}
          disabled={disabled}
          onClick={() => append(newRule(TPL.RULESET))}
        >
          Include ruleset
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DataObjectIcon />}
          disabled={disabled}
          onClick={() => append(newExprRule())}
        >
          Expression
        </Button>
        <AddRuleMenu onPick={(tpl) => append(newRule(tpl))} disabled={disabled} />
      </Stack>

      {errors?.['*'] && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          {errors['*']}
        </Typography>
      )}

      {value.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No rules — matches {emptyMatchesLabel}.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {value.map((rule, index) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              expanded={expandedIds.has(rule.id)}
              onToggleExpanded={() => setExpanded(rule.id, !expandedIds.has(rule.id))}
              onChange={(patch) => replaceAt(index, { ...rule, ...patch })}
              onReplace={(next) => replaceAt(index, next)}
              onRemove={() => onChange(value.filter((_, i) => i !== index))}
              tenantId={tenantId}
              excludeRulesetId={excludeRulesetId}
              error={errors?.[String(index)]}
              warning={warnings?.[String(index)]}
              disabled={disabled}
              contextSchemas={contextSchemas}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
